import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin } from 'lucide-react';
import Image from 'next/image';
import type { Update } from '@/lib/types';

export function TestimonialCard({ testimonial }: { testimonial: Update }) {
  return (
    <Card className="border-border shadow-soft bg-card rounded-[2.5rem] overflow-hidden group hover:shadow-soft-hover transition-all duration-700 h-full flex flex-col">
      <CardHeader className="p-0">
        <div className="aspect-square relative overflow-hidden bg-secondary/30">
          {testimonial.coverImage ? (
            <Image 
              src={testimonial.coverImage} 
              alt={testimonial.author || 'Family testimonial'} 
              fill 
              className="object-cover group-hover:scale-105 transition-transform duration-1000" 
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                <Star className="w-12 h-12 text-primary/10" />
            </div>
          )}
          <div className="absolute top-6 left-6">
            <Badge className="bg-card/90 backdrop-blur-md text-primary px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest border-none">Happy Tail</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-8 flex-grow flex flex-col space-y-4">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <Star 
                key={i} 
                className={`w-4 h-4 ${i < (testimonial.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} 
            />
          ))}
        </div>
        
        <blockquote className="text-xl font-headline font-bold text-foreground leading-tight italic">
          &ldquo;{testimonial.title}&rdquo;
        </blockquote>
        
        <p className="text-sm text-muted-foreground font-medium line-clamp-4 flex-grow italic">
          {testimonial.excerpt || testimonial.content}
        </p>

        <div className="pt-4 border-t border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
                {testimonial.authorImage && (
                    <div className="w-8 h-8 rounded-full overflow-hidden relative border border-border">
                        <Image src={testimonial.authorImage} alt={testimonial.authorName || 'User'} fill className="object-cover" />
                    </div>
                )}
                <div>
                    <p className="text-sm font-black text-foreground">{testimonial.authorName || testimonial.author || 'Anonymous Family'}</p>
                    {testimonial.location && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">{testimonial.location}</span>
                        </div>
                    )}
                </div>
            </div>
            {testimonial.category && (
                <Badge variant="secondary" className="bg-secondary/50 text-[9px] font-black uppercase tracking-tighter rounded-lg">
                    {testimonial.category}
                </Badge>
            )}
        </div>
      </CardContent>
    </Card>
  );
}
